/**
 * Сводка — таблица «метрики × месяцы», как лист «Динамика по году» в Excel.
 * Все показатели считаются автоматически из expenses/revenue_facts/objects.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import {
  fetchFinanceSummary, formatRub,
  type SummaryMetric,
} from "@/api/finance";
import { ApiError } from "@/api/client";
import { usePeriod } from "@/state/period";


const RU_MONTH_SHORT: Record<string, string> = {
  "01": "Янв", "02": "Фев", "03": "Мар", "04": "Апр", "05": "Май", "06": "Июн",
  "07": "Июл", "08": "Авг", "09": "Сен", "10": "Окт", "11": "Ноя", "12": "Дек",
};


export function FinanceSummaryPage() {
  const { region } = usePeriod();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(2025);

  const q = useQuery({
    queryKey: ["finance-summary", year, region],
    queryFn: () => fetchFinanceSummary(year, region !== "all" ? region : undefined),
    staleTime: 60 * 1000,
  });

  if (q.isLoading) return <Spinner size="lg" label="Загрузка сводки…" />;
  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ только для админа/руководителя</div></div>;
    }
    const msg = q.error instanceof ApiError ? `HTTP ${q.error.status}` : (q.error as Error).message;
    return <div className="alert alert-red"><div className="alert-body">{msg}</div></div>;
  }
  const data = q.data!;

  // Группировка метрик
  const groups: Record<string, SummaryMetric[]> = {};
  for (const m of data.metrics) {
    (groups[m.group] = groups[m.group] || []).push(m);
  }
  const groupOrder = ["Выручка", "Расходы", "Маржа", "Удельные"];

  function fmtCell(v: number, fmt: SummaryMetric["format"]): string {
    if (v === 0) return "—";
    if (fmt === "rub") return formatRub(v);
    if (fmt === "pct") return `${v.toFixed(1)}%`;
    if (fmt === "int") return String(Math.round(v));
    if (fmt === "sqm") return `${v.toFixed(0)}`;
    return String(v);
  }

  return (
    <FinanceLayout>
      
      <div className="filter-bar" style={{ marginBottom: "var(--gap)" }}>
        <select className="input-sm" value={year} onChange={e => setYear(Number(e.target.value))} style={{ minWidth: 120 }}>
          {[currentYear, currentYear - 1, currentYear - 2, currentYear + 1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: "var(--mute)" }}>
          Все показатели считаются автоматически из таблиц приходов, расходов и объектов
        </span>
      </div>

      <div className="card" style={{ overflow: "auto" }}>
        <table className="tbl" style={{ fontSize: 12.5, minWidth: 1100 }}>
          <thead>
            <tr>
              <th style={{ position: "sticky", left: 0, background: "var(--paper)", minWidth: 280, zIndex: 2 }}>Метрика</th>
              {data.months.map(m => {
                const month = m.split("-")[1];
                return <th key={m} className="num" style={{ width: 90 }}>{RU_MONTH_SHORT[month]}</th>;
              })}
              <th className="num strong" style={{ width: 110, background: "var(--paper-2)" }}>Год</th>
            </tr>
          </thead>
          <tbody>
            {groupOrder.map(group => {
              const items = groups[group] || [];
              if (items.length === 0) return null;
              return (
                <>
                  <tr key={`group-${group}`} style={{ background: "var(--paper-2)" }}>
                    <td colSpan={data.months.length + 2} style={{ fontWeight: 600, color: "var(--ink-2)" }}>
                      {group}
                    </td>
                  </tr>
                  {items.map(m => (
                    <tr key={m.key} style={m.bold ? { fontWeight: 700 } : undefined}>
                      <td style={{ position: "sticky", left: 0, background: "var(--paper)", zIndex: 1 }}>
                        {m.label}
                      </td>
                      {m.values.map((v, i) => (
                        <td key={i} className="num tabular" style={{ color: v === 0 ? "var(--mute)" : undefined, fontSize: 12 }}>
                          {fmtCell(v, m.format)}
                        </td>
                      ))}
                      <td className="num tabular strong" style={{ background: "var(--paper-2)" }}>
                        {fmtCell(m.year_total, m.format)}
                      </td>
                    </tr>
                  ))}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </FinanceLayout>
  );
}
